import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import { getAdjustmentReasonList } from "services/data.internal.service";
import { deactivateAdjustmentReason } from "services/data.internal.service";
import { ActiveButton } from "ui-components/IconButton";
import { activeAdjustmentReason } from "services/data.inventory.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { getAdjustmentReasonStatusCount } from "services/data.inventory.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function AdjustmentReasonList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["adjustment-reason-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission =
    allowAll ||
    (Boolean(permissions["Adjustment Reason"]) &&
      permissions["Adjustment Reason"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["Adjustment Reason"]) &&
      permissions["Adjustment Reason"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["Adjustment Reason"]) &&
      permissions["Adjustment Reason"].findIndex((b) => b === "deactivate") !== -1);

  useQuery({
    queryKey: ["adjustment-reason-list", { customerCode, active }],
    queryFn: () => getAdjustmentReasonList({ active }),
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["adjustment-reason-list", "status", { customerCode }],
    queryFn: () => getAdjustmentReasonStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateAdjustmentReason, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activeAdjustmentReason, {
    onSuccess,
    onError,
  });

  const onNew = () => navigate("/setup/adjustment-reason/add");

  const onEdit = (req) => {
    navigate("/setup/adjustment-reason/edit", {
      state: req,
    });
  };

  const handleClose = () => setDialog(null);

  const handleOk = (req) => {
    delMutation.mutate(req);
    setDialog(null);
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.reasonCode}`}
        onClose={handleClose}
        onOk={() => {
          handleOk(req);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.reasonCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Reason Code", accessorKey: "reasonCode" },
    { header: "Description 1", accessorKey: "description1" },
    { header: "Description 2", accessorKey: "description2" },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { reasonCode } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit adjustment reason"
                disabled={!editPermission}
                onClick={() => {
                  onEdit({ reasonCode });
                }}
              />
              <DelButton
                title="deactivate adjustment reason"
                disabled={!deactivatePermission}
                onClick={() => {
                  onDelete({ reasonCode });
                }}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate adjustment reason"
              disabled={!editPermission}
              onClick={() => onActive({ reasonCode })}
            />
          );
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;adjustment&nbsp;reason
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default AdjustmentReasonList;
